import type { LidControl } from "../model/inp/lidControls.ts";
import type { LidUsageRow } from "../model/inp/lidUsage.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

/** [LID_USAGE] for one subcatchment. Unlike Coverages, more than one row for the same LID control is allowed, so rows are keyed by array index rather than a unique field. */
export function LidUsageEditor({
  subcatchmentName,
  lidControls,
  usage,
  onChange,
}: {
  subcatchmentName: string;
  lidControls: LidControl[];
  usage: LidUsageRow[];
  onChange: (rows: LidUsageRow[]) => void;
}) {
  const t = useT();

  function addRow() {
    if (lidControls.length === 0) return;
    onChange([
      ...usage,
      {
        subcatchment: subcatchmentName,
        lidControl: lidControls[0].name,
        number: 1,
        area: 0,
        width: 0,
        initSat: 0,
        fromImperv: 0,
        toPerv: false,
        fromPerv: 0,
        reportFile: "",
        drainTo: "",
      },
    ]);
  }

  function patchAt(index: number, fields: Partial<LidUsageRow>) {
    onChange(usage.map((row, i) => (i === index ? { ...row, ...fields } : row)));
  }

  function removeAt(index: number) {
    onChange(usage.filter((_, i) => i !== index));
  }

  if (lidControls.length === 0) {
    return (
      <section className="property-panel-group">
        <h3>{t("lidUsage.title")}</h3>
        <p className="property-panel-hint">{t("lidUsage.noControlsYet")}</p>
      </section>
    );
  }

  return (
    <section className="property-panel-group">
      <h3>{t("lidUsage.title")}</h3>
      {usage.length === 0 && <p className="property-panel-hint">{t("lidUsage.noUsage")}</p>}
      {usage.length > 0 && (
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("lidUsage.lidControl")}</th>
                <th>{t("lidUsage.number")}</th>
                <th>{t("lidUsage.area")}</th>
                <th>{t("lidUsage.width")}</th>
                <th>{t("lidUsage.initSat")}</th>
                <th>{t("lidUsage.fromImperv")}</th>
                <th>{t("lidUsage.toPerv")}</th>
                <th>{t("lidUsage.fromPerv")}</th>
                <th>{t("lidUsage.drainTo")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {usage.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={row.lidControl}
                      onChange={(e) => patchAt(index, { lidControl: e.target.value })}
                    >
                      {lidControls.map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <NumberInput
                      value={row.number}
                      onChange={(v) => patchAt(index, { number: v })}
                    />
                  </td>
                  <td>
                    <NumberInput value={row.area} onChange={(v) => patchAt(index, { area: v })} />
                  </td>
                  <td>
                    <NumberInput value={row.width} onChange={(v) => patchAt(index, { width: v })} />
                  </td>
                  <td>
                    <NumberInput
                      value={row.initSat}
                      onChange={(v) => patchAt(index, { initSat: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={row.fromImperv}
                      onChange={(v) => patchAt(index, { fromImperv: v })}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.toPerv}
                      onChange={(e) => patchAt(index, { toPerv: e.target.checked })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={row.fromPerv}
                      onChange={(v) => patchAt(index, { fromPerv: v })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.drainTo}
                      onChange={(e) => patchAt(index, { drainTo: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button-icon button-icon-danger"
                      onClick={() => removeAt(index)}
                      aria-label={t("lidUsage.deleteUsage")}
                      title={t("lidUsage.deleteUsage")}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className="button" onClick={addRow}>
        {t("lidUsage.addUsage")}
      </button>
    </section>
  );
}
